(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class NeedAChatHeader extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.menuButton = this.querySelector('[data-menu-toggle]');
      this.mobilePanel = this.querySelector('[data-mobile-panel]');
      this.desktopDisclosures = [...this.querySelectorAll('.site-nav__details')];
      this.updateHeaderHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
        const observedHeaderBar = this.querySelector('.site-header__bar');

        if (observedHeaderBar) {
          this.resizeObserver.observe(observedHeaderBar);
        }

        const observedAnnouncement = this.querySelector('.site-header__announcement');

        if (observedAnnouncement) {
          this.resizeObserver.observe(observedAnnouncement);
        }
      }

      if (this.menuButton && this.mobilePanel) {
        this.menuButton.addEventListener('click', () => this.toggleMenu(), { signal });
        this.mobilePanel.addEventListener('click', (event) => {
          if (event.target.closest('a[href]')) this.closeMenu(false);
        }, { signal });
      }

      this.desktopDisclosures.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.desktopDisclosures.forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
        }, { signal });
      });

      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal });
      document.addEventListener('pointerdown', (event) => this.onDocumentPointerDown(event), { signal });
      window.addEventListener('resize', () => {
        if (window.matchMedia('(min-width: 68.01rem)').matches) this.closeMenu(false);
      }, { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      document.body.classList.remove('menu-open');
    }

    updateHeaderHeight() {
      const announcement = this.querySelector('.site-header__announcement');
      const headerBar = this.querySelector('.site-header__bar');

      const announcementHeight = announcement
        ? announcement.getBoundingClientRect().height
        : 0;

      const headerBarHeight = headerBar
        ? headerBar.getBoundingClientRect().height
        : 0;

      const height = Math.ceil(announcementHeight + headerBarHeight);

      if (height > 0) {
        document.documentElement.style.setProperty(
          '--header-height',
          `${height}px`
        );
      }
    }

    get isOpen() {
      return this.menuButton?.getAttribute('aria-expanded') === 'true';
    }

    toggleMenu() {
      if (this.isOpen) {
        this.closeMenu(true);
      } else {
        this.openMenu();
      }
    }

    openMenu() {
      if (!this.menuButton || !this.mobilePanel) return;
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.menuButton.setAttribute('aria-label', this.menuButton.dataset.closeLabel || 'Close menu');
      this.mobilePanel.hidden = false;
      document.body.classList.add('menu-open');

      const firstFocusable = this.mobilePanel.querySelector(focusableSelector);
      requestAnimationFrame(() => firstFocusable?.focus());
    }

    closeMenu(returnFocus = true) {
      if (!this.menuButton || !this.mobilePanel || !this.isOpen) return;
      this.menuButton.setAttribute('aria-expanded', 'false');
      this.menuButton.setAttribute('aria-label', this.menuButton.dataset.openLabel || 'Open menu');
      this.mobilePanel.hidden = true;
      document.body.classList.remove('menu-open');
      this.mobilePanel.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      if (returnFocus) this.menuButton.focus();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        const openDesktopDisclosure = this.desktopDisclosures.find((details) => details.open);
        if (openDesktopDisclosure) {
          openDesktopDisclosure.removeAttribute('open');
          openDesktopDisclosure.querySelector('summary')?.focus();
          return;
        }

        if (this.isOpen) {
          event.preventDefault();
          this.closeMenu(true);
        }
      }

      if (event.key === 'Tab' && this.isOpen && this.mobilePanel) {
        const focusable = [...this.mobilePanel.querySelectorAll(focusableSelector)]
          .filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          this.menuButton.focus();
        } else if (event.shiftKey && document.activeElement === this.menuButton) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === this.menuButton) {
          event.preventDefault();
          first.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          this.menuButton.focus();
        }
      }
    }

    onDocumentPointerDown(event) {
      if (!this.contains(event.target)) {
        this.closeMenu(false);
        this.desktopDisclosures.forEach((details) => details.removeAttribute('open'));
      }
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.input = this.querySelector('input[type="number"]');
      this.minus = this.querySelector('[data-quantity-change="minus"]');
      this.plus = this.querySelector('[data-quantity-change="plus"]');
      if (!this.input) return;

      this.minus?.addEventListener('click', () => this.change('down'), { signal });
      this.plus?.addEventListener('click', () => this.change('up'), { signal });
      this.input.addEventListener('input', () => this.updateButtons(), { signal });
      this.input.addEventListener('change', () => this.normalize(), { signal });
      this.updateButtons();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    change(direction) {
      if (!this.input) return;
      this.normalize();

      try {
        direction === 'up' ? this.input.stepUp() : this.input.stepDown();
      } catch (_error) {
        const step = this.numberAttribute('step', 1);
        const current = Number(this.input.value) || this.numberAttribute('min', 1);
        this.input.value = String(direction === 'up' ? current + step : current - step);
        this.normalize();
      }

      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateButtons();
    }

    normalize() {
      if (!this.input) return;
      const min = this.numberAttribute('min', 1);
      const max = this.input.max === '' ? Infinity : this.numberAttribute('max', Infinity);
      const step = this.numberAttribute('step', 1);
      let value = Number(this.input.value);

      if (!Number.isFinite(value)) value = min;
      value = Math.max(min, Math.min(max, value));
      if (step > 0 && Number.isFinite(step)) {
        const stepped = min + Math.round((value - min) / step) * step;
        value = Math.max(min, Math.min(max, stepped));
      }

      this.input.value = String(value);
      this.updateButtons();
    }

    numberAttribute(name, fallback) {
      const value = Number(this.input?.getAttribute(name));
      return Number.isFinite(value) ? value : fallback;
    }

    updateButtons() {
      if (!this.input) return;
      const value = Number(this.input.value);
      const min = this.numberAttribute('min', 1);
      const max = this.input.max === '' ? Infinity : this.numberAttribute('max', Infinity);
      if (this.minus) this.minus.disabled = Number.isFinite(value) && value <= min;
      if (this.plus) this.plus.disabled = Number.isFinite(value) && value >= max;
    }
  }

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.variantSelect = this.querySelector('[data-variant-select]');
      this.productForm = this.querySelector('form[action*="/cart/add"]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addButtonText = this.querySelector('[data-add-to-cart-text]');
      this.status = this.querySelector('[data-product-status]');
      this.quantityInput = this.querySelector('[data-product-quantity]');
      this.price = this.querySelector('[data-product-price]');
      this.mediaItems = [...document.querySelectorAll(`[data-product-section="${CSS.escape(this.dataset.sectionId || '')}"] [data-media-id]`)];

      const dataNode = this.querySelector('[data-variant-data]');
      try {
        this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
      } catch (_error) {
        this.variants = [];
      }

      this.variantSelect?.addEventListener('change', () => this.onVariantChange(), { signal });
      this.onVariantChange(false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    onVariantChange(updateUrl = true) {
      const variantId = Number(this.variantSelect?.value || this.productForm?.querySelector('[name="id"]')?.value);
      const variant = this.variants.find((item) => Number(item.id) === variantId);
      this.updateVariant(variant, updateUrl);
    }

    updateVariant(variant, updateUrl) {
      const unavailableLabel = this.dataset.unavailableLabel || 'Unavailable';
      const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      const addLabel = this.dataset.addLabel || 'Add to cart';

      if (!variant) {
        if (this.addButton) this.addButton.disabled = true;
        if (this.addButtonText) this.addButtonText.textContent = unavailableLabel;
        if (this.status) {
          this.status.textContent = unavailableLabel;
          this.status.dataset.state = 'unavailable';
        }
        return;
      }

      this.updatePrice(variant);
      this.updateQuantityRules(variant);
      this.updateMedia(variant.featured_media_id);

      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.addButtonText) this.addButtonText.textContent = variant.available ? addLabel : soldOutLabel;
      if (this.status) {
        this.status.textContent = variant.available ? (variant.available_label || '') : soldOutLabel;
        this.status.dataset.state = variant.available ? 'available' : 'sold-out';
      }

      if (updateUrl && this.dataset.updateUrl !== 'false' && variant.url && window.history?.replaceState) {
        window.history.replaceState({}, '', variant.url);
      }

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    updatePrice(variant) {
      if (!this.price) return;
      const regular = this.price.querySelector('[data-price-regular]');
      const sale = this.price.querySelector('[data-price-sale]');
      const unit = this.price.querySelector('[data-price-unit]');
      const onSale = Number(variant.compare_at_price_amount) > Number(variant.price_amount);

      if (regular) {
        regular.textContent = onSale ? variant.compare_at_price : variant.price;
        regular.classList.toggle('price__regular--sale', onSale);
      }
      if (sale) {
        sale.textContent = onSale ? variant.price : '';
        sale.hidden = !onSale;
      }
      if (unit) {
        unit.textContent = variant.unit_price_label || '';
        unit.hidden = !variant.unit_price_label;
      }
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const min = Number(variant.quantity_min) || 1;
      const step = Number(variant.quantity_increment) || 1;
      const max = variant.quantity_max == null ? null : Number(variant.quantity_max);

      this.quantityInput.min = String(min);
      this.quantityInput.step = String(step);
      if (Number.isFinite(max)) {
        this.quantityInput.max = String(max);
      } else {
        this.quantityInput.removeAttribute('max');
      }

      const current = Number(this.quantityInput.value);
      if (!Number.isFinite(current) || current < min || (Number.isFinite(max) && current > max)) {
        this.quantityInput.value = String(min);
      }

      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateMedia(mediaId) {
      if (!mediaId || !this.mediaItems.length) return;
      this.mediaItems.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId)));
    }
  }

  const focusReturnedFormStatus = () => {
    const status = document.querySelector('[data-form-status][tabindex="-1"]');
    if (status && !status.closest('[hidden]')) status.focus({ preventScroll: false });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', focusReturnedFormStatus, { once: true });
  } else {
    focusReturnedFormStatus();
  }

  if (!customElements.get('need-a-chat-header')) {
    customElements.define('need-a-chat-header', NeedAChatHeader);
  }
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }
  if (!customElements.get('product-info')) {
    customElements.define('product-info', ProductInfo);
  }
})();
